import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User, Cpu } from 'lucide-react';
import ClosingCTA from '../components/ClosingCTA';

const steps = [
  { owner: 'developer', label: 'New requirement arrives' },
  { owner: 'agent', label: 'Drafts test plan' },
  { owner: 'developer', label: 'Reviews and approves test plan', critical: true },
  { owner: 'agent', label: 'Implements the tests' },
  { owner: 'agent', label: 'Implements logic; prepares test report' },
  { owner: 'agent', label: 'Runs unit / integration / E2E suites before raising PR' },
  { owner: 'agent', label: 'Fixes regression failures; compiles regression report' },
  { owner: 'developer', label: 'Reviews test results and regression report; signs off', critical: true },
];

export default function NeuralWorksHowItWorks() {
  return (
    <div>
      <section className="pt-16 pb-10">
        <div className="dwx">
          <Link to="/neuralworks" className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60 hover:text-[#1A1A1A]">
            <ArrowLeft size={14} /> Back to NeuralWorks
          </Link>
          <div className="mt-8 mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F]">Layer 1 · Specification & Assurance</div>
          <h1 className="serif-tight text-[54px] md:text-[92px] leading-[0.94] mt-4">Tests as Spec.</h1>
          <h2 className="serif-tight text-[32px] md:text-[46px] leading-[1.02] text-[#1A1A1A]/55 mt-2 max-w-3xl">The flight plan for agent delivery.</h2>

          <div className="mt-14 grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-7 text-[17px] leading-relaxed text-[#1A1A1A]/85">
              <p>Test-driven development has been the right idea for twenty years and too expensive for most teams to sustain. Spec-driven development is the open problem in AI engineering right now. Tests as Spec sits between them.</p>
              <p className="mt-4">If an agent’s failure mode is misunderstanding requirements, then the requirement — not the code — is the artefact that must be hardened. So every requirement ships with a test plan, and a person reviews that plan before a line of code exists.</p>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9">
              <div className="rounded-[16px] bg-[#F4F2EC] p-6 border border-[#1A1A1A]/10">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/60 mb-4">Legend</div>
                <div className="flex items-center gap-2 text-[13px] mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#6E93B8]/20 border border-[#6E93B8]"><Cpu size={13} className="text-[#6E93B8]" /></span> Agent step</div>
                <div className="flex items-center gap-2 text-[13px] mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#C4633F]/15 border border-[#C4633F]"><User size={13} className="text-[#C4633F]" /></span> Developer step</div>
                <div className="flex items-center gap-2 text-[13px]"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#22A94F]/20 border border-[#22A94F] text-[#178838]">★</span> Critical review point</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-16">
        <div className="dwx">
          <div className="rounded-[24px] bg-[#FBFAF6] border border-[#1A1A1A]/10 p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-8 gap-3">
              {steps.map((s, i) => {
                const isAgent = s.owner === 'agent';
                return (
                  <div key={i} className={`relative rounded-[14px] p-4 border ${isAgent ? 'bg-[#6E93B8]/10 border-[#6E93B8]/40 border-dashed' : 'bg-[#C4633F]/10 border-[#C4633F]/40'}`}>
                    <div className="flex items-center justify-between">
                      <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Step {i + 1}</div>
                      {s.critical && <span className="text-[#178838]">★</span>}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      {isAgent ? <Cpu size={14} className="text-[#6E93B8]" /> : <User size={14} className="text-[#C4633F]" />}
                      <span className="mono text-[10px] uppercase tracking-wider text-[#1A1A1A]/70">{s.owner}</span>
                    </div>
                    <div className="mt-3 text-[13px] leading-snug text-[#1A1A1A]">{s.label}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-[16px] bg-[#F4F2EC] p-6">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#178838] mb-2">The load-bearing moment</div>
                <div className="text-[14px] leading-relaxed text-[#1A1A1A]/80">Step three. A developer reads the AI-generated test plan and checks it against what was actually asked for, including what was implied and not written down. It is the one step we will not automate.</div>
              </div>
              <div className="rounded-[16px] bg-[#F4F2EC] p-6">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#A63D2F] mb-2">What it costs you</div>
                <div className="text-[14px] leading-relaxed text-[#1A1A1A]/80">Existing features need unit, integration and E2E coverage before agents can safely change them. Business analysts have to validate the E2E tests, including edge cases. This is real work and it takes months on a large estate.</div>
              </div>
              <div className="rounded-[16px] bg-[#F4F2EC] p-6">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#5B4FCF] mb-2">What you get</div>
                <div className="text-[14px] leading-relaxed text-[#1A1A1A]/80">Developers stop writing code and start owning requirements. One engineer runs several features and fixes through agents at once, in the way a product owner runs a backlog. The regression net holds while they do it.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
